/**
 * First Business Group — Sector detail content (bilingual FR/EN).
 * Content is illustrative/realistic based on publicly known DRC & Central Africa
 * market data. Client should review and replace sensitive numbers with their
 * own proprietary figures before marketing-grade publication.
 */

export const SECTORS = {
  mining: {
    iconName: 'Mountain',
    color: '#DB242C',
    accent: '#1B325F',
    slug: 'mining',
    fr: {
      hero: {
        kicker: 'Mines & Ressources',
        title: "Le cœur minéral de l'Afrique",
        tagline:
          "La RDC abrite les plus grandes réserves mondiales de cobalt et l'une des plus importantes en cuivre. FBG accompagne investisseurs et opérateurs dans l'exploration, l'acquisition de permis et les partenariats stratégiques.",
        cta: 'Discuter d\'un projet minier',
      },
      kpis: [
        { label: 'du cobalt mondial', value: '~70%' },
        { label: 'exports miniers/an', value: '$24B+' },
        { label: 'permis actifs', value: '1,100+' },
        { label: 'grands opérateurs', value: '15+' },
      ],
      overview: {
        title: 'Pourquoi la RDC maintenant',
        body: "La transition énergétique mondiale (véhicules électriques, stockage batterie, électronique) crée une demande sans précédent pour les minerais stratégiques dont la RDC est le premier producteur mondial : cobalt, coltan, cuivre de haute pureté. Les corridors logistiques Lobito (Angola) et Dar es Salaam (Tanzanie) ouvrent de nouvelles voies d'export, tandis que le code minier de 2018 a clarifié les règles pour les investisseurs étrangers.",
      },
      opportunities: [
        {
          title: 'Cobalt & Lithium',
          desc: 'Approvisionnement stratégique pour l\'industrie des batteries EV et du stockage d\'énergie. Gisements dans le Katanga (Kolwezi, Likasi).',
        },
        {
          title: 'Cuivre',
          desc: 'Cathodes de haute pureté, demande soutenue par l\'électrification mondiale. Opportunités dans la Copperbelt DRC-Zambie.',
        },
        {
          title: 'Or & Diamants',
          desc: 'Ituri, Maniema, Kasaï. Artisanal et industriel. Formalisation et traçabilité responsable.',
        },
        {
          title: 'Minerais rares (Coltan, Cassitérite)',
          desc: 'Traçabilité ITSCI / iTSCi, chaînes d\'approvisionnement conformes Dodd-Frank et EU Regulation.',
        },
      ],
      caseStudies: [
        {
          client: 'Groupe industriel du Golfe',
          scope: 'Structuration d\'une joint-venture avec un opérateur local pour l\'exploration de 420 km² de permis cobalt-cuivre au Lualaba.',
          outcome: 'JV signée en 9 mois. Due diligence environnementale et communautaire complétée.',
        },
        {
          client: 'Trader minier asiatique',
          scope: 'Audit de 3 cibles d\'acquisition, négociation et conformité Code Minier 2018.',
          outcome: 'Acquisition finalisée ; +$40M d\'investissement déployé en phase 1.',
        },
      ],
      services: [
        'Acquisition et transfert de permis miniers',
        'Joint-ventures avec opérateurs locaux',
        'Conformité au Code Minier RDC (2018)',
        'Due diligence environnementale et sociale',
        'Relations gouvernementales (DGRAD, CAMI, SAEMAPE)',
        'Structuration fiscale et douanière à l\'export',
      ],
      seoTitle: 'Mines en RDC — Cobalt, Cuivre, Or | First Business Group',
      seoDescription:
        'Investissez dans les mines de la RDC avec FBG. Cobalt, cuivre, or, coltan. Acquisition de permis, joint-ventures, conformité Code Minier 2018.',
    },
    en: {
      hero: {
        kicker: 'Mining & Resources',
        title: "Africa's mineral heart",
        tagline:
          "The DRC holds the world's largest cobalt reserves and among the largest copper deposits. FBG guides investors and operators through exploration, permit acquisition and strategic partnerships.",
        cta: 'Discuss a mining project',
      },
      kpis: [
        { label: 'of global cobalt', value: '~70%' },
        { label: 'mining exports/year', value: '$24B+' },
        { label: 'active permits', value: '1,100+' },
        { label: 'major operators', value: '15+' },
      ],
      overview: {
        title: 'Why DRC, why now',
        body: "The global energy transition (EVs, battery storage, electronics) is driving unprecedented demand for strategic minerals where DRC leads worldwide production: cobalt, coltan, high-purity copper. The Lobito corridor (Angola) and Dar es Salaam corridor (Tanzania) open new export routes, while the 2018 Mining Code has clarified rules for foreign investors.",
      },
      opportunities: [
        {
          title: 'Cobalt & Lithium',
          desc: 'Strategic supply for EV batteries and energy storage. Katanga deposits (Kolwezi, Likasi).',
        },
        {
          title: 'Copper',
          desc: 'High-purity cathodes. Demand sustained by global electrification. Opportunities in DRC-Zambia Copperbelt.',
        },
        {
          title: 'Gold & Diamonds',
          desc: 'Ituri, Maniema, Kasaï. Artisanal and industrial. Responsible formalization and traceability.',
        },
        {
          title: 'Rare minerals (Coltan, Cassiterite)',
          desc: 'ITSCI / iTSCi traceability, Dodd-Frank and EU-compliant supply chains.',
        },
      ],
      caseStudies: [
        {
          client: 'Gulf industrial group',
          scope: 'Joint-venture structuring with a local operator to explore 420 km² of cobalt-copper permits in Lualaba.',
          outcome: 'JV signed in 9 months. Environmental and community due diligence completed.',
        },
        {
          client: 'Asian mining trader',
          scope: 'Audit of 3 acquisition targets, negotiation and 2018 Mining Code compliance.',
          outcome: 'Acquisition closed; +$40M deployed in phase 1.',
        },
      ],
      services: [
        'Mining permit acquisition and transfer',
        'Joint-ventures with local operators',
        '2018 DRC Mining Code compliance',
        'Environmental and social due diligence',
        'Government relations (DGRAD, CAMI, SAEMAPE)',
        'Export tax and customs structuring',
      ],
      seoTitle: 'DRC Mining — Cobalt, Copper, Gold | First Business Group',
      seoDescription:
        'Invest in DRC mining with FBG. Cobalt, copper, gold, coltan. Permit acquisition, joint-ventures, 2018 Mining Code compliance.',
    },
  },

  logistics: {
    iconName: 'Truck',
    color: '#1B325F',
    accent: '#DB242C',
    slug: 'logistics',
    fr: {
      hero: {
        kicker: 'Logistique',
        title: 'Les corridors qui relient le continent',
        tagline:
          "De Dubaï à Kinshasa en passant par Lobito, FBG structure des chaînes d'approvisionnement résilientes reliant le Golfe à l'Afrique centrale.",
        cta: 'Optimiser votre supply chain',
      },
      kpis: [
        { label: 'corridor Lobito', value: '1,300 km' },
        { label: 'ports RDC', value: '3' },
        { label: 'temps moyen UAE→DRC', value: '21 jours' },
        { label: 'freight partners', value: '30+' },
      ],
      overview: {
        title: 'Le désenclavement est une opportunité',
        body: "La RDC, immense et enclavée, investit massivement dans ses infrastructures de transport : corridor Lobito (remis en service 2024, financé US-UE), port en eau profonde de Banana en construction, réhabilitation de la route nationale 1. Pour les opérateurs capables de gérer la complexité douanière et logistique, les marges sont significatives.",
      },
      opportunities: [
        {
          title: 'Corridor Lobito',
          desc: 'Angola → RDC → Zambie. Priorité stratégique US/EU. Ideal pour l\'export minerai et l\'import équipement.',
        },
        {
          title: 'Hub Dubaï ↔ Afrique centrale',
          desc: 'Fret aérien et maritime Jebel Ali vers Matadi, Pointe-Noire, Dar es Salaam.',
        },
        {
          title: 'Entreposage & cross-docking',
          desc: 'Zones franches DRC (Maluku), Dubaï (JAFZA). Optimisation fiscale et douanière.',
        },
        {
          title: 'Transport multimodal',
          desc: 'Route + rail + fleuve Congo. Solutions pour équipements lourds, conteneurs et produits périssables.',
        },
      ],
      caseStudies: [
        {
          client: 'Distributeur EAU',
          scope: "Mise en place d'un flux mensuel Jebel Ali → Matadi → Kinshasa pour 40 conteneurs.",
          outcome: 'Délai réduit de 35 à 22 jours. Économies douanières de 12%.',
        },
        {
          client: 'Opérateur minier',
          scope: 'Évacuation de minerai via le corridor Lobito (nouveau vs. Dar es Salaam).',
          outcome: 'Coût/tonne -18%, délai -9 jours sur la chaîne complète.',
        },
      ],
      services: [
        'Dédouanement import / export (OGEFREM, DGDA)',
        'Freight forwarding maritime et aérien',
        'Gestion de corridors (Lobito, Matadi, Dar es Salaam)',
        'Entreposage zones franches DRC et UAE',
        'Consulting supply chain end-to-end',
        'Assurance cargaison et gestion des risques',
      ],
      seoTitle: 'Logistique RDC & Corridor Lobito | First Business Group',
      seoDescription:
        'Supply chain et logistique RDC-UAE. Corridor Lobito, dédouanement, fret maritime et aérien. Solutions bout-en-bout par FBG.',
    },
    en: {
      hero: {
        kicker: 'Logistics',
        title: 'Corridors that connect the continent',
        tagline:
          'From Dubai to Kinshasa via Lobito, FBG builds resilient supply chains linking the Gulf to Central Africa.',
        cta: 'Optimize your supply chain',
      },
      kpis: [
        { label: 'Lobito corridor', value: '1,300 km' },
        { label: 'DRC ports', value: '3' },
        { label: 'avg. UAE→DRC transit', value: '21 days' },
        { label: 'freight partners', value: '30+' },
      ],
      overview: {
        title: 'Breaking the landlock is an opportunity',
        body: "DRC, vast and landlocked, is investing heavily in transport infrastructure: Lobito corridor (reopened 2024, US/EU-funded), deep-water Banana port under construction, National Road 1 rehabilitation. For operators who can navigate customs and logistics complexity, margins are significant.",
      },
      opportunities: [
        {
          title: 'Lobito Corridor',
          desc: 'Angola → DRC → Zambia. US/EU strategic priority. Ideal for mineral export and equipment import.',
        },
        {
          title: 'Dubai ↔ Central Africa hub',
          desc: 'Air and sea freight from Jebel Ali to Matadi, Pointe-Noire, Dar es Salaam.',
        },
        {
          title: 'Warehousing & cross-docking',
          desc: 'Free zones DRC (Maluku) and Dubai (JAFZA). Tax and customs optimization.',
        },
        {
          title: 'Multimodal transport',
          desc: 'Road + rail + Congo River. Solutions for heavy equipment, containers, perishables.',
        },
      ],
      caseStudies: [
        {
          client: 'UAE distributor',
          scope: 'Monthly Jebel Ali → Matadi → Kinshasa flow for 40 containers.',
          outcome: 'Transit cut from 35 to 22 days. 12% customs savings.',
        },
        {
          client: 'Mining operator',
          scope: 'Ore evacuation via Lobito corridor (new vs. Dar es Salaam).',
          outcome: 'Cost per ton -18%, timeline -9 days end-to-end.',
        },
      ],
      services: [
        'Import/export customs clearance (OGEFREM, DGDA)',
        'Ocean and air freight forwarding',
        'Corridor management (Lobito, Matadi, Dar es Salaam)',
        'Free zone warehousing DRC & UAE',
        'End-to-end supply chain consulting',
        'Cargo insurance and risk management',
      ],
      seoTitle: 'DRC Logistics & Lobito Corridor | First Business Group',
      seoDescription:
        'DRC-UAE supply chain and logistics. Lobito corridor, customs clearance, ocean and air freight. End-to-end solutions by FBG.',
    },
  },

  infrastructure: {
    iconName: 'Landmark',
    color: '#DB242C',
    accent: '#1B325F',
    slug: 'infrastructure',
    fr: {
      hero: {
        kicker: 'Infrastructure',
        title: "Bâtir les fondations d'un continent",
        tagline:
          "Routes, ports, énergie, logements : FBG vous connecte aux projets d'infrastructure prioritaires en RDC et aux mécanismes PPP du gouvernement.",
        cta: 'Explorer les appels d\'offres',
      },
      kpis: [
        { label: 'déficit d\'infrastructure RDC', value: '$50B+' },
        { label: 'PPP en préparation', value: '30+' },
        { label: 'potentiel hydroélectrique', value: '100 GW' },
        { label: 'population à desservir', value: '100M+' },
      ],
      overview: {
        title: 'Le plus grand chantier du siècle',
        body: "La RDC a un déficit d'infrastructure estimé à plus de 50 milliards USD. Le gouvernement a structuré un agenda clair : énergie (Inga III, mini-réseaux solaires), transport (routes, ports, aéroports), urbain (logement social, eau potable). Les montages PPP et BOT sont activement promus, avec garanties souveraines et appui des bailleurs multilatéraux (Banque mondiale, BAD).",
      },
      opportunities: [
        {
          title: 'Énergie & Inga',
          desc: 'Hydroélectricité Inga III (11 GW), solaire, mini-réseaux ruraux. Demande électrique en forte croissance.',
        },
        {
          title: 'Routes & ponts',
          desc: 'Route nationale 1, ponts sur le fleuve Congo, désenclavement provincial.',
        },
        {
          title: 'Ports & aéroports',
          desc: 'Port en eau profonde de Banana, modernisation Ndjili, Kavumu, Goma.',
        },
        {
          title: 'Logement & urbain',
          desc: 'Programmes de logement social. Kinshasa prévoit +300k unités d\'ici 2030.',
        },
      ],
      caseStudies: [
        {
          client: 'Consortium EPC du Golfe',
          scope: 'Positionnement sur un appel d\'offres énergie de $850M ; structuration BOT.',
          outcome: 'Pré-qualification obtenue ; partenariat local structuré.',
        },
        {
          client: 'Fonds d\'infrastructure africain',
          scope: 'Investissement dans un PPP routier de 420 km.',
          outcome: 'Closing financier en 14 mois ; garantie partielle de la BAD obtenue.',
        },
      ],
      services: [
        'Identification et qualification d\'appels d\'offres publics',
        'Structuration PPP / BOT / BOOT',
        'Ingénierie financière et souscription de garanties',
        'Relations avec ministères (ITPR, Énergie, Finances)',
        'Structuration de consortiums local + international',
        'Suivi réglementaire (ARSP, ANAPI)',
      ],
      seoTitle: 'Infrastructure RDC — PPP, Énergie, Routes | FBG',
      seoDescription:
        'Projets d\'infrastructure en RDC : énergie Inga, routes, ports, logements. Structuration PPP et accès aux appels d\'offres via FBG.',
    },
    en: {
      hero: {
        kicker: 'Infrastructure',
        title: "Building a continent's foundations",
        tagline:
          'Roads, ports, energy, housing: FBG connects you to DRC\'s priority infrastructure projects and government PPP mechanisms.',
        cta: 'Explore tenders',
      },
      kpis: [
        { label: 'DRC infrastructure gap', value: '$50B+' },
        { label: 'PPPs in pipeline', value: '30+' },
        { label: 'hydro potential', value: '100 GW' },
        { label: 'population to serve', value: '100M+' },
      ],
      overview: {
        title: 'The largest construction opportunity of the century',
        body: "DRC's infrastructure gap exceeds USD 50 billion. The government has a clear agenda: energy (Inga III, solar mini-grids), transport (roads, ports, airports), urban (social housing, potable water). PPP and BOT structures are actively promoted, with sovereign guarantees and multilateral support (World Bank, AfDB).",
      },
      opportunities: [
        {
          title: 'Energy & Inga',
          desc: 'Inga III hydro (11 GW), solar, rural mini-grids. Rapidly growing electricity demand.',
        },
        {
          title: 'Roads & bridges',
          desc: 'National Road 1, Congo River bridges, provincial connectivity.',
        },
        {
          title: 'Ports & airports',
          desc: 'Banana deep-water port, modernization of Ndjili, Kavumu, Goma.',
        },
        {
          title: 'Housing & urban',
          desc: 'Social housing programs. Kinshasa plans 300k+ units by 2030.',
        },
      ],
      caseStudies: [
        {
          client: 'Gulf EPC consortium',
          scope: '$850M energy tender positioning; BOT structuring.',
          outcome: 'Pre-qualified; local partnership structured.',
        },
        {
          client: 'African infrastructure fund',
          scope: '420 km road PPP investment.',
          outcome: 'Financial close in 14 months; AfDB partial guarantee secured.',
        },
      ],
      services: [
        'Public tender identification and qualification',
        'PPP / BOT / BOOT structuring',
        'Financial engineering and guarantee underwriting',
        'Ministry relations (ITPR, Energy, Finance)',
        'Local + international consortium building',
        'Regulatory oversight (ARSP, ANAPI)',
      ],
      seoTitle: 'DRC Infrastructure — PPP, Energy, Roads | FBG',
      seoDescription:
        'DRC infrastructure projects: Inga energy, roads, ports, housing. PPP structuring and tender access through FBG.',
    },
  },

  'trade-finance': {
    iconName: 'Banknote',
    color: '#1B325F',
    accent: '#DB242C',
    slug: 'trade-finance',
    fr: {
      hero: {
        kicker: 'Finance Commerciale',
        title: 'Débloquer les flux commerciaux',
        tagline:
          "Lettres de crédit, garanties, financement export : FBG vous connecte aux banques et institutions qui financent réellement le commerce RDC-UAE.",
        cta: 'Structurer un financement',
      },
      kpis: [
        { label: 'commerce bilatéral RDC-UAE', value: '$3B+' },
        { label: 'banques partenaires', value: '12' },
        { label: 'durée moyenne d\'une LC', value: '90-180j' },
        { label: 'taux acceptance typique', value: '85%+' },
      ],
      overview: {
        title: 'La liquidité qui manque souvent au deal',
        body: "De nombreux deals RDC-Golfe avortent non pas sur le prix ou la qualité, mais sur la structuration financière. Lettres de crédit refusées, garanties manquantes, méconnaissance des mécanismes Afreximbank ou Banque Centrale du Congo. FBG intervient comme arrangeur et structureur auprès de banques locales, régionales et internationales.",
      },
      opportunities: [
        {
          title: 'Lettres de crédit (LC)',
          desc: 'Export minerai, import équipement. LC confirmées par banques de premier rang Dubaï / Londres.',
        },
        {
          title: 'Garanties de soumission & bonne fin',
          desc: 'Pour les appels d\'offres publics et privés. Bid bonds, performance bonds, advance payment guarantees.',
        },
        {
          title: 'Financement pré-export',
          desc: 'Avances sur stocks, financement de récolte / production. Typiquement avec Afreximbank, TDB, banques locales.',
        },
        {
          title: 'Assurance crédit export',
          desc: 'Couverture des risques politiques et commerciaux via Atradius, Coface, MIGA.',
        },
      ],
      caseStudies: [
        {
          client: 'Exportateur cobalt',
          scope: 'Structuration LC confirmée de $12M pour cargaison vers acheteur chinois via trader UAE.',
          outcome: 'LC émise en 11 jours ; paiement sécurisé à la présentation des documents.',
        },
        {
          client: 'Importateur équipement industriel',
          scope: 'Financement import $4.5M sur 180 jours avec garantie souveraine partielle.',
          outcome: 'Taux négocié -180bps vs. offre initiale.',
        },
      ],
      services: [
        'Structuration de lettres de crédit (LC) import et export',
        'Émission de garanties bancaires (bid, performance, advance)',
        'Financement pré-export et post-shipment',
        'Accès Afreximbank, TDB, banques locales RDC',
        'Assurance crédit export (Coface, Atradius, MIGA)',
        'Conformité KYC / AML / FATCA',
      ],
      seoTitle: 'Finance Commerciale RDC — Lettres de Crédit, Garanties | FBG',
      seoDescription:
        'Financement du commerce RDC-UAE : lettres de crédit, garanties bancaires, financement pré-export. Afreximbank, TDB, banques locales.',
    },
    en: {
      hero: {
        kicker: 'Trade Finance',
        title: 'Unlocking trade flows',
        tagline:
          'Letters of credit, guarantees, export finance: FBG connects you to banks and institutions that actually fund DRC-UAE trade.',
        cta: 'Structure a financing',
      },
      kpis: [
        { label: 'DRC-UAE bilateral trade', value: '$3B+' },
        { label: 'banking partners', value: '12' },
        { label: 'typical LC tenor', value: '90-180d' },
        { label: 'typical acceptance rate', value: '85%+' },
      ],
      overview: {
        title: 'The missing liquidity that kills deals',
        body: "Many DRC-Gulf deals fail not on price or quality, but on financial structuring. Rejected letters of credit, missing guarantees, unfamiliarity with Afreximbank or Central Bank of Congo mechanisms. FBG acts as arranger and structurer with local, regional and international banks.",
      },
      opportunities: [
        {
          title: 'Letters of Credit (LC)',
          desc: 'Mineral export, equipment import. LCs confirmed by top-tier banks in Dubai / London.',
        },
        {
          title: 'Bid & performance bonds',
          desc: 'For public and private tenders. Bid bonds, performance bonds, advance payment guarantees.',
        },
        {
          title: 'Pre-export financing',
          desc: 'Inventory advances, harvest/production financing. Typically with Afreximbank, TDB, local banks.',
        },
        {
          title: 'Export credit insurance',
          desc: 'Political and commercial risk cover via Atradius, Coface, MIGA.',
        },
      ],
      caseStudies: [
        {
          client: 'Cobalt exporter',
          scope: '$12M confirmed LC for shipment to Chinese buyer via UAE trader.',
          outcome: 'LC issued in 11 days; payment secured on document presentation.',
        },
        {
          client: 'Industrial equipment importer',
          scope: '$4.5M import financing over 180 days with partial sovereign guarantee.',
          outcome: 'Rate negotiated -180bps vs. initial offer.',
        },
      ],
      services: [
        'Import/export Letter of Credit structuring',
        'Bank guarantees (bid, performance, advance)',
        'Pre-export and post-shipment financing',
        'Access to Afreximbank, TDB, DRC local banks',
        'Export credit insurance (Coface, Atradius, MIGA)',
        'KYC / AML / FATCA compliance',
      ],
      seoTitle: 'DRC Trade Finance — Letters of Credit, Guarantees | FBG',
      seoDescription:
        'DRC-UAE trade financing: letters of credit, bank guarantees, pre-export financing. Afreximbank, TDB, local banks.',
    },
  },

  'agri-business': {
    iconName: 'Wheat',
    color: '#DB242C',
    accent: '#1B325F',
    slug: 'agri-business',
    fr: {
      hero: {
        kicker: 'Agribusiness',
        title: 'Nourrir un continent qui double',
        tagline:
          "80 millions d'hectares arables, climat varié, main-d'œuvre jeune : la RDC est l'un des derniers grands réservoirs agricoles mondiaux. FBG vous aide à le déverrouiller.",
        cta: 'Lancer un projet agricole',
      },
      kpis: [
        { label: 'hectares arables', value: '80M' },
        { label: 'exploités aujourd\'hui', value: '<10%' },
        { label: 'climats/micro-zones', value: '5' },
        { label: 'import alimentaire/an', value: '$1.5B' },
      ],
      overview: {
        title: 'De l\'importateur net au champion régional',
        body: "La RDC importe aujourd'hui pour $1.5B de produits alimentaires que le pays pourrait théoriquement produire. Le gouvernement pousse les zones agro-industrielles (Bukanga-Lonzo, Kolwezi), les concessions long-terme et les PPP agricoles. Les opérateurs du Golfe, avec capital et expertise, sont des partenaires recherchés.",
      },
      opportunities: [
        {
          title: 'Cultures d\'exportation',
          desc: 'Café arabica (Kivu), cacao fin (Équateur), huile de palme, caoutchouc. Marchés Dubaï, UE, Chine.',
        },
        {
          title: 'Sécurité alimentaire',
          desc: 'Maïs, riz, manioc, volaille. Substitution d\'imports ; demande urbaine en forte croissance.',
        },
        {
          title: 'Transformation agroalimentaire',
          desc: 'Huileries, minoteries, abattoirs, chaîne du froid. Marges importantes en local.',
        },
        {
          title: 'Agriculture mécanisée',
          desc: 'Concessions de 5,000-50,000 ha. Partenariats avec concessionnaires historiques.',
        },
      ],
      caseStudies: [
        {
          client: 'Groupe agroalimentaire émirien',
          scope: 'Acquisition de 12,000 ha pour production céréalière et volaille ; structuration JV.',
          outcome: 'Concession signée, phase 1 en production à 18 mois.',
        },
        {
          client: 'Trader de café',
          scope: 'Sourcing direct Kivu, certification bio/FLO, export Dubaï / Rotterdam.',
          outcome: 'Chaîne complète établie ; 600T/an en année 2.',
        },
      ],
      services: [
        'Acquisition / bail emphytéotique de terres (concession)',
        'Autorisations d\'exploitation et d\'export agricole',
        'Partenariats agro-industriels et coopératives locales',
        'Certification qualité (bio, FLO, Fair Trade, HACCP)',
        'Accès à la BIAC, FONER et financements concessionnels',
        'Structuration zones agro-industrielles (ZAI)',
      ],
      seoTitle: 'Agribusiness RDC — Terres, Café, Cacao | First Business Group',
      seoDescription:
        'Investir dans l\'agriculture en RDC : concessions, café, cacao, palm, transformation. 80M ha arables. Accompagnement FBG.',
    },
    en: {
      hero: {
        kicker: 'Agri-business',
        title: "Feeding a continent that's doubling",
        tagline:
          '80 million arable hectares, varied climate, young workforce: DRC is among the world\'s last great agricultural reservoirs. FBG helps you unlock it.',
        cta: 'Launch an agri project',
      },
      kpis: [
        { label: 'arable hectares', value: '80M' },
        { label: 'currently farmed', value: '<10%' },
        { label: 'climate zones', value: '5' },
        { label: 'food imports/year', value: '$1.5B' },
      ],
      overview: {
        title: 'From net importer to regional champion',
        body: "DRC currently imports USD 1.5B of food it could theoretically produce. The government promotes agro-industrial zones (Bukanga-Lonzo, Kolwezi), long-term concessions, and agricultural PPPs. Gulf operators, with capital and expertise, are sought-after partners.",
      },
      opportunities: [
        {
          title: 'Export crops',
          desc: 'Arabica coffee (Kivu), fine cocoa (Équateur), palm oil, rubber. Markets: Dubai, EU, China.',
        },
        {
          title: 'Food security',
          desc: 'Maize, rice, cassava, poultry. Import substitution; rapidly growing urban demand.',
        },
        {
          title: 'Agri-processing',
          desc: 'Oil mills, flour mills, abattoirs, cold chain. Strong local margins.',
        },
        {
          title: 'Mechanized farming',
          desc: '5,000-50,000 ha concessions. Partnerships with legacy concession holders.',
        },
      ],
      caseStudies: [
        {
          client: 'Emirati agri-food group',
          scope: 'Acquisition of 12,000 ha for grain and poultry; JV structuring.',
          outcome: 'Concession signed, phase 1 producing at 18 months.',
        },
        {
          client: 'Coffee trader',
          scope: 'Direct Kivu sourcing, organic/FLO certification, export to Dubai / Rotterdam.',
          outcome: 'Full chain established; 600T/year by year 2.',
        },
      ],
      services: [
        'Land acquisition / long-term lease (concession)',
        'Agricultural operating and export permits',
        'Agri-industrial partnerships and local coops',
        'Quality certification (organic, FLO, Fair Trade, HACCP)',
        'Access to BIAC, FONER, concessional finance',
        'Agro-industrial zone (ZAI) structuring',
      ],
      seoTitle: 'DRC Agribusiness — Land, Coffee, Cocoa | First Business Group',
      seoDescription:
        'Invest in DRC agriculture: concessions, coffee, cocoa, palm, processing. 80M arable hectares. FBG support.',
    },
  },

  'digital-transformation': {
    iconName: 'Cpu',
    color: '#1B325F',
    accent: '#DB242C',
    slug: 'digital-transformation',
    fr: {
      hero: {
        kicker: 'Transformation Numérique',
        title: "L'Afrique saute directement à l'ère numérique",
        tagline:
          "100 millions d'habitants, adoption mobile massive, leapfrogging vers le paiement digital et l'e-gouvernance. FBG vous positionne sur l'une des dernières frontières tech.",
        cta: 'Explorer les opportunités tech',
      },
      kpis: [
        { label: 'pénétration mobile', value: '~55%' },
        { label: 'utilisateurs mobile money', value: '30M+' },
        { label: 'startups RDC', value: '150+' },
        { label: 'croissance fintech annuelle', value: '~35%' },
      ],
      overview: {
        title: 'Un marché en leapfrog',
        body: "La RDC saute l'étape de la banque traditionnelle pour passer directement au mobile money (Airtel Money, M-Pesa, Orange Money) et à la fintech. Le gouvernement a lancé la stratégie nationale du numérique 2025, la modernisation de la DGRAD et de la DGDA, et un cadre plus clair pour les données personnelles. Opportunités massives pour fournisseurs cloud, fintech, edtech, healthtech.",
      },
      opportunities: [
        {
          title: 'Fintech & mobile money',
          desc: 'Paiement, crédit instantané, assurance micro. Partenariats avec les opérateurs télécoms.',
        },
        {
          title: 'E-gouvernance',
          desc: 'Digitalisation douanes (DGDA), impôts (DGI), état civil, permis. Appels d\'offres réguliers.',
        },
        {
          title: 'Cloud & connectivité',
          desc: 'Datacenters, fibre optique (projet Equiano), satellites (Starlink autorisé 2024).',
        },
        {
          title: 'Edtech & Healthtech',
          desc: 'Apprentissage à distance, télémédecine, dossiers médicaux électroniques. Forte demande urbaine.',
        },
      ],
      caseStudies: [
        {
          client: 'Fintech MENA',
          scope: 'Entrée sur le marché RDC via partenariat avec un opérateur télécom local.',
          outcome: 'Licence BCC obtenue ; 450k utilisateurs actifs à 12 mois.',
        },
        {
          client: 'Intégrateur IT du Golfe',
          scope: 'Appel d\'offres de modernisation d\'un ministère ($8M).',
          outcome: 'Attribution via consortium local ; déploiement en cours.',
        },
      ],
      services: [
        'Licences réglementaires (BCC, ARPTC)',
        'Partenariats avec opérateurs télécoms (Airtel, Orange, Vodacom, Africell)',
        'Appels d\'offres e-gouvernance',
        'Structuration joint-ventures tech',
        'Conformité protection données et cybersécurité',
        'Accès à l\'écosystème startup (Kinshasa Digital, Ingenious City)',
      ],
      seoTitle: 'Fintech & Transformation Numérique RDC | First Business Group',
      seoDescription:
        'Fintech, mobile money, e-gouvernance, cloud en RDC. Licences BCC, partenariats télécoms, appels d\'offres. Accompagnement FBG.',
    },
    en: {
      hero: {
        kicker: 'Digital Transformation',
        title: 'Africa leapfrogs straight to digital',
        tagline:
          '100 million people, massive mobile adoption, leapfrogging to digital payments and e-government. FBG positions you on one of the last great tech frontiers.',
        cta: 'Explore tech opportunities',
      },
      kpis: [
        { label: 'mobile penetration', value: '~55%' },
        { label: 'mobile money users', value: '30M+' },
        { label: 'DRC startups', value: '150+' },
        { label: 'fintech YoY growth', value: '~35%' },
      ],
      overview: {
        title: 'A leapfrog market',
        body: "DRC skips traditional banking and jumps straight to mobile money (Airtel Money, M-Pesa, Orange Money) and fintech. The government launched the 2025 National Digital Strategy, modernization of DGRAD and DGDA, and a clearer data privacy framework. Massive opportunities for cloud providers, fintech, edtech, healthtech.",
      },
      opportunities: [
        {
          title: 'Fintech & mobile money',
          desc: 'Payments, instant credit, micro-insurance. Partnerships with telecom operators.',
        },
        {
          title: 'E-government',
          desc: 'Digitalization of customs (DGDA), taxes (DGI), civil registry, permits. Regular tenders.',
        },
        {
          title: 'Cloud & connectivity',
          desc: 'Data centers, fiber optic (Equiano), satellites (Starlink authorized 2024).',
        },
        {
          title: 'Edtech & Healthtech',
          desc: 'Remote learning, telemedicine, electronic medical records. Strong urban demand.',
        },
      ],
      caseStudies: [
        {
          client: 'MENA fintech',
          scope: 'DRC market entry via local telecom partnership.',
          outcome: 'BCC license obtained; 450k active users at 12 months.',
        },
        {
          client: 'Gulf IT integrator',
          scope: 'Ministry modernization tender ($8M).',
          outcome: 'Won via local consortium; deployment under way.',
        },
      ],
      services: [
        'Regulatory licensing (BCC, ARPTC)',
        'Telecom operator partnerships (Airtel, Orange, Vodacom, Africell)',
        'E-government tender support',
        'Tech joint-venture structuring',
        'Data protection and cybersecurity compliance',
        'Access to startup ecosystem (Kinshasa Digital, Ingenious City)',
      ],
      seoTitle: 'DRC Fintech & Digital Transformation | First Business Group',
      seoDescription:
        'Fintech, mobile money, e-government, cloud in DRC. BCC licenses, telecom partnerships, tenders. FBG support.',
    },
  },
};

export const SECTOR_KEYS = Object.keys(SECTORS);
